import { useEffect, useRef, useState } from 'react';
import { ApiError, call, metaApi } from '../api';

function saveFile(name: string, body: BlobPart, type: string) {
  const url = URL.createObjectURL(new Blob([body], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Back-up van alle instellingen (met of zonder wachtwoord), terugzetten en logs downloaden (alleen de eigenaar). */
export function BackupSettings() {
  const [owner, setOwner] = useState(false);
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [pending, setPending] = useState<unknown>(null);
  const [restorePw, setRestorePw] = useState('');
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    metaApi.providers().then((r) => setOwner(r.canManage)).catch(() => setOwner(false));
  }, []);

  if (!owner) return null;

  const encrypted = async () => {
    if (pw.length < 12) return setMsg({ text: 'Kies een wachtwoord van minstens 12 tekens.', error: true });
    if (pw !== pw2) return setMsg({ text: 'De twee wachtwoorden zijn niet gelijk.', error: true });
    setBusy(true);
    setMsg(null);
    try {
      const r = await call<unknown>('/api/backup/encrypted', { method: 'POST', body: JSON.stringify({ password: pw }) });
      saveFile(`tumedia-backup-${new Date().toISOString().slice(0, 10)}.tumedia`, JSON.stringify(r), 'application/json');
      setPw('');
      setPw2('');
      setMsg({ text: 'Versleutelde back-up gedownload. Bewaar het wachtwoord goed: zonder kan niemand hem openen, ook jij niet.' });
    } catch (err) {
      setMsg({ text: err instanceof ApiError && err.message !== err.code ? err.message : 'Back-up maken mislukt.', error: true });
    } finally {
      setBusy(false);
    }
  };

  const restore = async (data: unknown, password?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      const body = password ? { ...(data as object), password } : data;
      const r = await call<{ restored: string[]; skipped: string[]; themes: number }>('/api/backup', { method: 'POST', body: JSON.stringify(body) });
      setPending(null);
      setRestorePw('');
      setMsg({
        text: `Teruggezet: ${r.restored.length} onderdelen${r.themes ? ` en ${r.themes} skins` : ''}.${r.skipped.length ? ` Niet gelukt (versleuteld met een andere sleutel): ${r.skipped.join(', ')}.` : ''} Ververs de pagina.`,
        error: r.skipped.length > 0,
      });
    } catch (err) {
      setMsg({ text: err instanceof ApiError && err.message !== err.code ? err.message : 'Terugzetten mislukt.', error: true });
    } finally {
      setBusy(false);
    }
  };

  const pick = async (f: File) => {
    try {
      const data = JSON.parse(await f.text()) as { format?: number };
      // Versleuteld: eerst om het wachtwoord vragen
      if (data.format === 2) {
        setPending(data);
        setMsg(null);
        return;
      }
      await restore(data);
    } catch {
      setMsg({ text: 'Dit bestand is geen geldige back-up.', error: true });
    }
  };

  const logs = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/admin/logs', { credentials: 'same-origin' });
      if (!res.ok) throw new Error();
      saveFile(`tumedia-logs-${new Date().toISOString().slice(0, 10)}.txt`, await res.text(), 'text/plain');
    } catch {
      setMsg({ text: 'Logs downloaden mislukt.', error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="settings-block" aria-label="Back-up">
        <h2>Back-up</h2>
        <p className="subtle hint">
          Alle instellingen in één bestand: IPTV-accounts, koppelingen, Seerr, Trakt, debrid, startschermen, skins, kijkvoorkeuren en kinderslot.
        </p>
        <h3 className="bk-sub">Versleuteld met wachtwoord (aanbevolen)</h3>
        <p className="subtle hint">Hiermee kun je alles terugzetten, ook op een nieuwe installatie. Zonder het wachtwoord is het bestand onbruikbaar.</p>
        <form className="bk-form" onSubmit={(e) => { e.preventDefault(); void encrypted(); }}>
          <label className="ua2-select">Wachtwoord (minstens 12 tekens)
            <input className="text-input" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
          </label>
          <label className="ua2-select">Nog een keer
            <input className="text-input" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </label>
          <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !pw}>{busy ? 'Bezig…' : 'Versleutelde back-up downloaden'}</button>
        </form>
        <h3 className="bk-sub">Zonder wachtwoord</h3>
        <p className="subtle hint">Wachtwoorden en sleutels blijven daarin versleuteld met de sleutel van deze installatie: alleen hier terug te zetten.</p>
        <div className="form-actions">
          <a className="btn btn-sm" href="/api/backup" download>Back-up downloaden</a>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => file.current?.click()}>{busy ? 'Bezig…' : 'Back-up terugzetten'}</button>
          <input ref={file} type="file" accept="application/json,.json,.tumedia" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ''; }} />
        </div>
        {pending !== null && (
          <form className="bk-form" onSubmit={(e) => { e.preventDefault(); void restore(pending, restorePw); }}>
            <label className="ua2-select">Wachtwoord van deze back-up
              <input className="text-input" type="password" autoComplete="current-password" autoFocus value={restorePw} onChange={(e) => setRestorePw(e.target.value)} />
            </label>
            <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !restorePw}>Terugzetten</button>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => setPending(null)}>Annuleren</button>
          </form>
        )}
        {msg && <p className={msg.error ? 'error' : 'subtle'} role="status">{msg.text}</p>}
      </section>
      <section className="settings-block" aria-label="Logs">
        <h2>Logs</h2>
        <p className="subtle hint">
          De laatste 5000 regels van de server, om een probleem uit te zoeken of te delen. Sleutels, tokens, wachtwoorden,
          IPTV-gegevens en e-mailadressen worden eruit gehaald. Kijk het bestand toch even na voor je het deelt.
        </p>
        <div className="form-actions">
          <button className="btn btn-sm" disabled={busy} onClick={() => void logs()}>Logs downloaden</button>
        </div>
      </section>
    </>
  );
}
